import { Button, ButtonProps } from "@chakra-ui/button";
import { Box, BoxProps, Text, Flex } from "@chakra-ui/layout";
import { Datepicker } from "@datepicker-react/styled";
import { OnDatesChangeProps } from "@datepicker-react/hooks";
import ReactDOM from "react-dom";
import { useRef, useState, useEffect } from "react";
import { CalendarIcon } from "@chakra-ui/icons";
import { useTheme } from "@chakra-ui/react";
import { Dayjs, dayjs } from "@tether-web-portal/dayjs-setup";
import { DATE_FORMATS } from "@tether-web-portal/constants/dateFormats";

export interface DatePickerProps extends BoxProps {
  date: Dayjs | null;
  placeholder?: string;
  onDateChange: (newDate: Dayjs | null) => void;
  minDate?: Dayjs | null;
  maxDate?: Dayjs | null;
  marginLeft?: number | string; // Shifts the calendar horizontally
  marginTop?: number | string; //shift the calendar up
  buttonProps?: Partial<ButtonProps>;
}

const hasParentClass = (child: HTMLInputElement, classname: string): boolean => {
  if (child.className.split && child.className.split(" ").indexOf(classname) >= 0) return true;
  try {
    //Throws TypeError if child doesn't have parent any more
    return !!child.parentNode && hasParentClass(child.parentNode as HTMLInputElement, classname);
  } catch (TypeError) {
    return false;
  }
};

export const DatePicker = ({
  date,
  placeholder,
  onDateChange,
  minDate,
  maxDate,
  marginLeft,
  marginTop,
  buttonProps,
  ...other
}: DatePickerProps) => {
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const theme = useTheme();
  const datePickerRef = useRef<HTMLButtonElement>(null);
  const documentClickHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const containerRef = useRef<Document | Element | null>(null);

  const hideDatePicker = () => {
    // Clean up any existing event listeners
    if (documentClickHandlerRef.current && containerRef.current) {
      containerRef.current.removeEventListener(
        "click",
        documentClickHandlerRef.current as EventListenerOrEventListenerObject,
      );
      documentClickHandlerRef.current = null;
      containerRef.current = null;
    }
    setDatePickerVisible(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      hideDatePicker();
    };
  }, []);

  const showDatePicker = () => {
    if (datePickerVisible) {
      return;
    }

    // Chakra's modal swallows clicks so if one's open whack the click handler on that instead
    const modalContainers = document.getElementsByClassName("chakra-modal__content-container");
    const container = modalContainers && modalContainers.length > 0 ? modalContainers[0] : document;
    containerRef.current = container;

    const documentClickHandler = (e: MouseEvent) => {
      if (!hasParentClass(e.target as HTMLInputElement, "date-picker-wrapper")) {
        // Ignore clicks on datepicker component
        hideDatePicker();
      }
    };

    documentClickHandlerRef.current = documentClickHandler;
    setDatePickerVisible(true);

    // Let current callstack finish then add background click listener
    setTimeout(() => {
      if (containerRef.current && documentClickHandlerRef.current) {
        containerRef.current.addEventListener(
          "click",
          documentClickHandlerRef.current as EventListenerOrEventListenerObject,
        );
      }
    });
  };

  const handleDatesChange = (data: OnDatesChangeProps) => {
    const prev = date || dayjs();
    const nextDate = dayjs(data.startDate);
    if (nextDate) {
      nextDate.hour(prev.hour());
      nextDate.minute(prev.minute());
    }
    onDateChange(nextDate);
    setDatePickerVisible(false);
  };

  // Position the floating calendar relative to the trigger. Default is directly
  // below it, but flip ABOVE when there isn't room below (e.g. a date field near
  // the bottom of a tall modal) so the calendar never spills past the viewport
  // edge and gets cut off. Also clamp horizontally so it can't run off the right.
  const triggerRect = datePickerVisible ? datePickerRef.current?.getBoundingClientRect() : undefined;
  const CALENDAR_HEIGHT = 360;
  const CALENDAR_WIDTH = 300;
  const VIEWPORT_GAP = 8;
  let calendarTop = 0;
  let calendarLeft = 0;
  if (triggerRect) {
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const openAbove = spaceBelow < CALENDAR_HEIGHT && triggerRect.top > spaceBelow;
    calendarTop = openAbove
      ? Math.max(VIEWPORT_GAP, triggerRect.top - CALENDAR_HEIGHT)
      : triggerRect.bottom;
    calendarLeft = Math.min(
      triggerRect.left,
      Math.max(VIEWPORT_GAP, window.innerWidth - CALENDAR_WIDTH - VIEWPORT_GAP),
    );
  }

  // Portal for DatePicker
  const datePickerPortal = datePickerVisible
    ? ReactDOM.createPortal(
        <Box
          boxShadow="2xl"
          borderRadius={2}
          className="date-picker-wrapper"
          position="fixed" // Use fixed positioning to place the date picker relative to the viewport
          top={`${calendarTop}px`}
          left={`${calendarLeft}px`}
          sx={{
            "*": {
              fontFamily: `${theme.fonts.body} !important`,
              boxShadow: "none !important",
            },
            marginLeft: marginLeft,
            marginTop: marginTop,
          }}
          zIndex="var(--chakra-zIndices-popover)"
          backgroundColor="white"
        >
          <Datepicker
            onDatesChange={handleDatesChange}
            onClose={hideDatePicker}
            startDate={null}
            endDate={date?.toDate() || null}
            focusedInput={"startDate"}
            numberOfMonths={1}
            minBookingDate={minDate?.toDate()}
            maxBookingDate={maxDate?.toDate()}
            showSelectedDates={false}
            showResetDates={false}
            showClose={false}
            initialVisibleMonth={(date || dayjs()).toDate()}
            onDayRender={(date) => (
              <Flex
                height="100%"
                border="1px dashed"
                borderColor={dayjs(date).isSame(dayjs(), "day") ? "blue.400" : "transparent"}
                justifyContent="center"
                alignItems="center"
              >
                <Text>{dayjs(date).date()}</Text>
              </Flex>
            )}
          />
        </Box>,
        document.body, // Append to body or another element outside your modal
      )
    : null;

  return (
    <Box borderRadius="md" position="relative" bg="white" color="gray.700" {...other}>
      <Button
        ref={datePickerRef}
        onClick={() => {
          datePickerVisible ? hideDatePicker() : showDatePicker();
        }}
        bg="transparent"
        leftIcon={<CalendarIcon />}
        variant="outline"
        {...buttonProps}
      >
        {date
          ? `${dayjs(date).format(DATE_FORMATS.DAY_MONTH_YEAR_SLASHED).toLowerCase()}`
          : placeholder || "Select date"}
      </Button>
      {datePickerPortal}
    </Box>
  );
};
